/**
 * Lazy imports that survive a deployment.
 *
 * A built page references its code chunks by content hash. Deploy again and the
 * hashes change, but a tab that was already open still holds the old
 * `index.html` and still asks for the old names. The request then fails and
 * React renders nothing but "Failed to fetch dynamically imported module",
 * which reads as a broken feature rather than as a page that is simply out of
 * date. Somebody who had the app open when a release went out sees a button
 * that does nothing, and reloading — the one thing that fixes it — is not an
 * obvious response to that message.
 *
 * So: on a failed chunk load, reload once. The reload fetches a fresh
 * `index.html`, whose hashes match what is actually deployed, and the feature
 * opens. `index.html` is served `max-age=0, must-revalidate`, so it genuinely
 * is fresh rather than another copy of the stale one.
 *
 * Once, and recorded in `sessionStorage` — if the chunk is missing for any
 * other reason (a bad deploy, a blocked request, a filtered network), reloading
 * repeatedly would spin forever and show nothing at all. The second failure is
 * allowed through to the error boundary, where it is at least visible.
 */

import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "fc:chunk-reload";

/** A missing chunk, as opposed to the module itself throwing on evaluation. */
function looksLikeAStaleChunk(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function alreadyTried(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_KEY) !== null;
  } catch {
    // Private windows and locked-down settings throw on access. Treat that as
    // "already tried" so a browser that cannot remember never loops.
    return true;
  }
}

function rememberAndReload(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, "1");
  } catch {
    return;
  }
  window.location.reload();
}

/**
 * Clear the marker once the app is running, so a stale chunk met later in the
 * same session can still recover. Without this, one recovered reload would use
 * up the single retry for the whole session.
 */
export function markChunksHealthy(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint; anything narrower rejects real components
export function lazyChunk<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    load().catch((error: unknown) => {
      if (looksLikeAStaleChunk(error) && !alreadyTried()) {
        rememberAndReload();
        // Never settles — the reload replaces the page. Rejecting here would
        // flash the error boundary in the moment before it does.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
}
